import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@illegally-parked/routes/Routes';
import { YourDetailsTemplate } from '@templates';
import { ChangeEvent, useEffect, useState } from 'react';
import { basename } from '@illegally-parked/routes/basename';
import { useParkingStore } from '@illegally-parked/store/store';
import {
  handleLoginPageEvent,
  handleLoginRequest,
  LoginRequestConfig,
  msalInstance,
  parseZodErrors,
  yourDetailsSchema,
} from '@utils';
import { useUserData } from '@hooks';
import { ErrorMessages } from '@types';
import { useHandleSubmit } from '../../../../hooks/useHandleSubmit';
import { ZodError } from 'zod';
import { UserDetails } from '../../../../types/userDetails';

export const YourDetails = () => {
  const navigate = useNavigate();
  const {
    userDetails,
    updateUserDetails,
    caseReferenceId,
    getDataForSubmission,
  } = useParkingStore();
  const [formData, setFormData] = useState<UserDetails>(userDetails);
  const [crmId, setCrmId] = useState<string>('');
  const [idProvider, setIdProvider] = useState<string>('');
  const { isLoggedInUser, contactData, isLoading, isFetching } =
    useUserData(crmId);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const isLoadingUserData = isLoading || isFetching;
  const { mutateAsync: submitForm, isPending } = useHandleSubmit();
  const [submitLoading, setSubmitLoading] = useState(false);
  const errorPath = 'yourDetails';

  useEffect(() => {
    if (!crmId) {
      handleLoginPageEvent(msalInstance, setCrmId, setIdProvider);
    }

    if (contactData) {
      const data = {
        email: contactData.emailaddress1 || '',
        firstName: contactData.firstname || '',
        lastName: contactData.lastname || '',
        contactId: crmId,
      };
      setFormData(data);
    }
  }, [contactData, crmId, idProvider]);

  const handleSubmit = async () => {
    const { firstName, lastName, email, contactId } = formData;
    try {
      const validatedData = yourDetailsSchema.parse({
        firstName,
        lastName,
        email,
      });
      updateUserDetails({
        ...validatedData,
        contactId,
      });
      const data = getDataForSubmission();
      await submitForm(data);
    } catch (error) {
      if (error instanceof ZodError) {
        const newErrorMessages = parseZodErrors(error);
        setErrorMessages(newErrorMessages);
      } else {
        setErrorMessages([
          {
            message: (error as Error).message,
            path: errorPath,
          },
        ]);
      }
      throw new Error();
    }
  };

  const handleNext = async () => {
    if (caseReferenceId) {
      setErrorMessages([
        {
          path: errorPath,
          message:
            'You have already submitted this report. Please return to the homepage.',
        },
      ]);
      return;
    }
    setSubmitLoading(true);
    try {
      await handleSubmit();
      navigate(RoutesList.CONFIRMATION);
      // add this to allow popstate event listener to be triggered
      window.history.pushState({}, '', basename + RoutesList.CONFIRMATION);
    } catch (error) {
      throw new Error();
    } finally {
      setSubmitLoading(false);
    }
  };

  const loginConfig: LoginRequestConfig = {
    msal: msalInstance,
    navigate: () => navigate(RoutesList.YOUR_DETAILS, { replace: true }),
  };

  const handleLogin = async () => {
    await handleLoginRequest(loginConfig);
  };

  const buttonPrevious = {
    disabled: isPending || submitLoading,
    onClick: () => {
      navigate(RoutesList.EMAIL_CONFIRMATION_REPORT);
    },
  };

  const buttonNext = {
    label: 'Submit',
    loading: isPending || submitLoading,
    onClick: handleNext,
  };
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
    setErrorMessages([]);
  };

  return (
    <YourDetailsTemplate
      isLoadingUserData={isLoadingUserData}
      isLoggedInUser={isLoggedInUser}
      userData={formData}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      errorMessages={errorMessages}
      handleLogin={handleLogin}
      onChange={handleChange}
      idProvider={idProvider}
    />
  );
};
